/**
 * WB4.7 — recoverability policy facade (reads ladder SSOT only).
 */

import type { RecoverabilityMode } from './workbench-action-safety-policy';
import {
  BULK_STATE_CHANGE_HANDLER_REFS,
  FINANCE_COMPENSATING_PREFIX,
  financeReversalHandlerRef,
  getWorkbenchRecoverabilityLadderEntry,
  isBulkStateChangeHandlerRef,
  parseFinanceReversalSourceVoucherId,
  resolveRecoverabilityFromPolicy,
  resolveRecoverabilityTierFromPolicy,
  type WorkbenchRecoverabilityLadderEntry,
} from './workbench-recoverability-ladder';

export type WorkbenchRecoverabilityTier =
  import('./workbench-action-safety-policy').WorkbenchRiskTier;

export {
  BULK_STATE_CHANGE_HANDLER_REFS,
  FINANCE_COMPENSATING_PREFIX,
  financeReversalHandlerRef,
  isBulkStateChangeHandlerRef,
  parseFinanceReversalSourceVoucherId,
  resolveRecoverabilityFromPolicy,
};

export {
  CORRECTION_THREAD_BLOCKING_REASON,
  CORRECTION_THREAD_STEP_ID,
  CORRECTION_APPLIED_THREAD_STEP_ID,
} from './operational-thread';

function policyForHandler(handlerRef: string) {
  return resolveRecoverabilityFromPolicy('', handlerRef);
}

export function isMediumTierHandlerRef(handlerRef: string): boolean {
  return policyForHandler(handlerRef).riskTier === 'medium';
}

export function isHighTierHandlerRef(handlerRef: string): boolean {
  return policyForHandler(handlerRef).riskTier === 'high';
}

export function isFinanceCompensatingHandlerRef(handlerRef: string): boolean {
  return policyForHandler(handlerRef).riskTier === 'finance';
}

export function resolveRecoverabilityTier(
  handlerRef: string
): WorkbenchRecoverabilityTier | undefined {
  return resolveRecoverabilityTierFromPolicy(handlerRef);
}

export function resolveRecoverabilityMode(handlerRef: string): RecoverabilityMode | undefined {
  const tier = resolveRecoverabilityTierFromPolicy(handlerRef);
  if (!tier) {
    return undefined;
  }
  return policyForHandler(handlerRef).recoverabilityMode;
}

export function isApprovalCorrectionHandlerRef(handlerRef: string): boolean {
  return policyForHandler(handlerRef).recoverabilityMode === 'approval_correction';
}

export function isRecoverabilityReversibleForHandler(handlerRef: string): boolean {
  const policy = policyForHandler(handlerRef);
  const entry = getWorkbenchRecoverabilityLadderEntry(policy.riskTier);
  return entry.allowsDirectRestore && policy.recoverabilityMode !== 'none';
}

export function requiresPopconfirmGate(handlerRef: string): boolean {
  return policyForHandler(handlerRef).confirmation !== 'none';
}

export function isToastUndoAllowedForHandler(handlerRef: string): boolean {
  const policy = policyForHandler(handlerRef);
  const entry = getWorkbenchRecoverabilityLadderEntry(policy.riskTier);
  return entry.allowsToastUndo && policy.recoverabilityMode === 'toast_undo';
}

export interface WorkbenchPopconfirmCopy {
  title: string;
  description?: string;
  okText: string;
  cancelText: string;
}

function ladderEntryForHandler(handlerRef: string): WorkbenchRecoverabilityLadderEntry {
  return getWorkbenchRecoverabilityLadderEntry(policyForHandler(handlerRef).riskTier);
}

export function resolvePopconfirmCopy(
  handlerRef: string,
  actionLabel?: string
): WorkbenchPopconfirmCopy {
  if (handlerRef === 'crud:delete') {
    return {
      title: '정말 삭제하시겠습니까?',
      description: '삭제 전 상태가 기록됩니다. 복구는 승인 보정 요청 경로로 진행됩니다.',
      okText: '삭제',
      cancelText: '취소',
    };
  }

  if (handlerRef.startsWith('flow:start:')) {
    const flowId = handlerRef.slice('flow:start:'.length);
    return {
      title: actionLabel ? `${actionLabel}을(를) 시작하시겠습니까?` : 'Flow를 시작하시겠습니까?',
      description: `Flow 템플릿(${flowId}) 실행 전 상태가 기록됩니다.`,
      okText: '시작',
      cancelText: '취소',
    };
  }

  if (isFinanceCompensatingHandlerRef(handlerRef)) {
    const sourceVoucherId = handlerRef.slice(FINANCE_COMPENSATING_PREFIX.length);
    return {
      title: '역분개(보상 트랜잭션)를 요청하시겠습니까?',
      description: `원전표 ${sourceVoucherId}에 대한 보상 경로가 기록됩니다.`,
      okText: '역분개 요청',
      cancelText: '취소',
    };
  }

  if (handlerRef === BULK_STATE_CHANGE_HANDLER_REFS.delete) {
    return {
      title: '선택 항목을 삭제하시겠습니까?',
      description: '삭제 전 상태가 기록됩니다. 복구는 승인 보정 요청 경로로 진행됩니다.',
      okText: '삭제',
      cancelText: '취소',
    };
  }

  if (handlerRef.startsWith('bulk:status:')) {
    const status = handlerRef.slice('bulk:status:'.length);
    return {
      title: actionLabel
        ? `${actionLabel}을(를) 실행하시겠습니까?`
        : '일괄 상태 변경을 실행하시겠습니까?',
      description: `선택 항목의 이전 상태(${status})가 기록됩니다.`,
      okText: '실행',
      cancelText: '취소',
    };
  }

  if (!ladderEntryForHandler(handlerRef).requiresPopconfirm) {
    return {
      title: actionLabel ? `${actionLabel}을(를) 실행하시겠습니까?` : '이 작업을 실행하시겠습니까?',
      okText: '실행',
      cancelText: '취소',
    };
  }

  return {
    title: actionLabel ? `${actionLabel}을(를) 실행하시겠습니까?` : '이 작업을 실행하시겠습니까?',
    okText: '실행',
    cancelText: '취소',
  };
}
